'use client'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'
import { range } from '@/utils/functions/range'
import { cn } from '@/utils/styles/cn'

interface IBrandPaginationProps {
    citySlug: string
    brandSlug: string
    page: number
    pageCount: number
}

export const BrandPagination = ({
    citySlug,
    brandSlug,
    page,
    pageCount,
}: IBrandPaginationProps) => {
    const locale = useLocale() as Locale
    const t = useTranslations()

    if (pageCount <= 1) return null

    const basePath = getLinkHref(
        {
            pattern: '/restaurants/[citySlug]/brands/[brandSlug]',
            params: { citySlug, brandSlug },
        },
        locale
    )

    return (
        <nav
            aria-label={t('restaurants.brand.paginationLabel')}
            className="flex flex-row flex-wrap justify-center gap-2 mt-10 px-4 md:px-0"
        >
            {range(pageCount).map((_, index) => {
                const pageNumber = index + 1
                const isCurrent = pageNumber === page

                return (
                    <Link
                        key={pageNumber}
                        href={pageNumber === 1 ? basePath : `${basePath}?page=${pageNumber}`}
                        aria-current={isCurrent ? 'page' : undefined}
                        className={cn(
                            'inline-flex items-center justify-center min-w-11 h-11 px-3 rounded-full',
                            'border-[1px] text-[14px] transition-colors',
                            isCurrent
                                ? 'border-black bg-black text-white'
                                : 'border-gray-200 hover:bg-gray-50'
                        )}
                    >
                        {pageNumber}
                    </Link>
                )
            })}
        </nav>
    )
}
